import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Storefront, ArrowSquareOut, FloppyDisk } from "@phosphor-icons/react";
import { Barcodes } from "@/components/admin/Barcodes";

const money = (n) => (n == null ? "--" : `$${Number(n).toFixed(2)}`);

export function PublicSiteSettings() {
  const [tab, setTab] = useState("rules");
  return (
    <div className="border border-[#E4E4E0] rounded-sm" data-testid="o4b-admin">
      <div className="px-4 py-3 border-b border-[#E4E4E0] flex items-center gap-3 flex-wrap">
        <Storefront size={16} className="text-[#E3000B]" />
        <span className="font-head font-bold uppercase text-xs tracking-wider text-gray-600">OFFERS4BRICKS · Public site</span>
        <a href="/" target="_blank" rel="noopener noreferrer" data-testid="o4b-open-site"
          className="ml-auto inline-flex items-center gap-1 h-7 px-2 border border-[#E4E4E0] rounded-sm font-mono text-[10px] hover:border-[#E3000B] hover:text-[#E3000B]">
          OPEN PUBLIC SITE <ArrowSquareOut size={12} />
        </a>
      </div>
      <div className="flex gap-2 border-b border-[#E4E4E0] px-4">
        {[["rules", "PUBLIC RULES & COPY"], ["quotes", "RECENT QUOTES"], ["barcodes", "BARCODES"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`o4b-admin-tab-${id}`}
            className={`px-3 h-9 border-b-2 font-head text-[11px] font-bold uppercase tracking-wider ${tab === id ? "border-[#E3000B] text-[#E3000B]" : "border-transparent text-gray-500 hover:text-black"}`}>{label}</button>
        ))}
      </div>
      <div className="p-4">
        {tab === "rules" && <Rules />}
        {tab === "quotes" && <Quotes />}
        {tab === "barcodes" && <Barcodes />}
      </div>
    </div>
  );
}

function Rules() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.publicSettings().then(setS).catch(() => toast.error("Failed to load")); }, []);
  if (!s) return null;
  const set = (k, v) => setS(x => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        site_name: s.site_name, headline: s.headline, pitch: s.pitch, contact_note: s.contact_note, contact_email: s.contact_email || "",
        requirements: (Array.isArray(s.requirements) ? s.requirements : String(s.requirements).split("\n")).map(x => x.trim()).filter(Boolean),
        max_sets_per_request: Number(s.max_sets_per_request),
        excluded_sets: Array.isArray(s.excluded_sets) ? s.excluded_sets : String(s.excluded_sets).split(/[\s,]+/).filter(Boolean),
        live_keepa_lookup: !!s.live_keepa_lookup,
        owner_alert_email: (s.owner_alert_email || "").trim(),
        local_pickup_enabled: s.local_pickup_enabled !== false,
        local_pickup_note: (s.local_pickup_note || "").trim(),
        quote_sets_per_request: Math.max(1, Number(s.quote_sets_per_request) || 5),
        quote_pc_concurrency: Math.max(1, Number(s.quote_pc_concurrency) || 4),
        quote_keepa_concurrency: Math.max(1, Number(s.quote_keepa_concurrency) || 6),
      };
      setS(await adminApi.savePublicSettings(patch)); toast.success("Public site rules saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const F = ({ label, k, hint, textarea, type = "text" }) => (
    <div>
      <Label className="font-mono text-[10px] uppercase text-gray-500">{label}</Label>
      {textarea ? <textarea value={Array.isArray(s[k]) ? s[k].join("\n") : (s[k] ?? "")} onChange={(e) => set(k, e.target.value)} rows={3}
          className="w-full mt-1 bg-[#F6F6F3] border border-[#E4E4E0] rounded-sm p-2 text-sm" data-testid={`o4b-rule-${k}`} />
        : <Input type={type} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-[#F6F6F3] border-[#E4E4E0]" data-testid={`o4b-rule-${k}`} />}
      {hint && <div className="font-mono text-[9px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="o4b-rules">
      <div className="space-y-4">
        <div className="font-head text-xs font-bold uppercase text-[#E3000B]">Limits (public site)</div>
        <div className="border border-[#E4E4E0] rounded-sm p-3 font-mono text-xs text-gray-700 bg-[#FFF9D6]" data-testid="o4b-rules-sitewide-note">
          Pricing rules (Smart Offer %, min ROI, high-value tier, no-Amazon tier, offer ceiling, minimum offer) now live in one place:
          <Link to="/settings" className="ml-1 font-bold text-[#0055BF] underline" data-testid="o4b-rules-sitewide-link">Settings → Sitewide Rules</Link>. They apply to Deals and the public site.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Max sets per request (0 = no limit)" k="max_sets_per_request" type="number" />
        </div>
        <F label="Excluded set numbers" k="excluded_sets" textarea hint="One per line or comma-separated — shown as NOT BUYING" />
        <div className="flex items-center gap-3 border border-[#E4E4E0] rounded-sm p-3">
          <Switch checked={!!s.live_keepa_lookup} onCheckedChange={(v) => set("live_keepa_lookup", v)} data-testid="o4b-rule-live-keepa" className="data-[state=checked]:bg-[#00A850]" />
          <span className="font-mono text-xs text-gray-800">Live Keepa lookup for unknown sets (≈10 tokens/set)</span>
        </div>
        <div className="font-head text-xs font-bold uppercase text-[#E3000B] pt-2">Pricing speed (throttle)</div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Sets per request" k="quote_sets_per_request" type="number" hint="Batch is split into requests of N sets, all sent at once" />
          <F label="PriceCharting at once" k="quote_pc_concurrency" type="number" hint="Server-wide parallel lookups" />
          <F label="Keepa at once" k="quote_keepa_concurrency" type="number" hint="Server-wide parallel lookups" />
        </div>
        <div className="font-head text-xs font-bold uppercase text-[#E3000B] pt-2">Local pickup / cash option</div>
        <div className="flex items-center gap-3">
          <Switch checked={s.local_pickup_enabled !== false} onCheckedChange={(v) => setS({ ...s, local_pickup_enabled: v })} className="data-[state=checked]:bg-[#E3000B]" data-testid="o4b-rule-local_pickup_enabled" />
          <span className="font-mono text-xs text-gray-800">Show "Local / Cash" as a 4th payout option (asks for city + state)</span>
        </div>
        <F label="Local pickup explanation" k="local_pickup_note" hint="Shown under the Local / Cash option on the public site" />
        <F label="Owner alert email" k="owner_alert_email" hint="Get a heads-up with the sets and total on every submission — blank = off" />
      </div>
      <div className="space-y-4">
        <div className="font-head text-xs font-bold uppercase text-[#E3000B]">Copy</div>
        <F label="Site name" k="site_name" />
        <F label="Headline" k="headline" />
        <F label="Pitch" k="pitch" textarea />
        <F label="What we're looking for (one per line)" k="requirements" textarea />
        <F label="After-accept note" k="contact_note" textarea />
        <F label="Contact email shown on customer invoices" k="contact_email" hint="Leave blank to hide" />
        <Button onClick={save} disabled={saving} data-testid="o4b-rules-save" className="h-9 rounded-sm bg-[#E3000B] text-white font-bold uppercase text-xs">
          <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function Quotes() {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminApi.quotes().then(r => setRows(r.rows)).catch(() => {}); }, []);
  return (
    <div className="border border-[#E4E4E0] rounded-sm overflow-x-auto" data-testid="o4b-quotes">
      <table className="w-full text-xs font-mono">
        <thead className="bg-[#F6F6F3] text-gray-500 uppercase text-[10px]"><tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Sets</th><th className="px-3 py-2 text-right">Total</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-gray-500">No quotes yet.</td></tr>}
          {rows.map(q => (
            <tr key={q.quote_id} className="border-t border-[#F6F6F3]">
              <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{new Date(q.created_at).toLocaleString()}</td>
              <td className="px-3 py-2">{q.items.map(i => <span key={i.set_number} className={`inline-block mr-2 ${i.offer ? "text-[#E3000B]" : "text-gray-400"}`}>{i.set_number}{i.offer ? ` ${money(i.offer)}` : ` (${i.status})`}</span>)}</td>
              <td className="px-3 py-2 text-right text-[#00A850]">{money(q.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
